import 'reflect-metadata';

import type { FC, ReactNode } from 'react';

import { store } from '@/redux/store';
import theme from '@/theme';
import {
  ChakraProvider,
  ColorModeScript,
  cookieStorageManagerSSR,
  localStorageManager,
} from '@chakra-ui/react';
import type { NextComponentType } from 'next';
import type { AppProps } from 'next/app';
import { Inter } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Provider as StateProvider } from 'react-redux';
import { ToastContainer } from 'react-toastify';

import '@/styles/globals.css';
import '@/styles/main.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'tailwindcss/tailwind.css';

import {
  DynamicBalanceProvider,
  DynamicInitializeAsyncStorage,
  DynamicOnLoadModule,
  DynamicRegularPageLayout,
  DynamicRouterHistoryProvider,
  DynamicSignerProvider,
  DynamicSolanaBalancesProvider,
  DynamicSolanaConnectProvider,
  DynamicSolanaJupiterProvider,
  DynamicSolanaSignerProvider,
  DynamicWeb3ModalProvider,
} from '@/ui/Components/DynamicComponents';

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  fallback: ['system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
});

interface Props extends Omit<AppProps, 'Component'> {
  Component: NextComponentType & {
    getLayout?: (page: ReactNode) => ReactNode;
    getHead?: () => ReactNode;
  };
  cookies?: string;
}

import { configReactI18next } from '@sofa/services/i18n';
import { useTranslation as useTranslation_ } from 'react-i18next';
configReactI18next({
  useTranslation: useTranslation_,
});

const SolanaWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <DynamicSolanaConnectProvider>
    <DynamicSolanaJupiterProvider>
      <DynamicSolanaSignerProvider>
        <DynamicSolanaBalancesProvider>{children}</DynamicSolanaBalancesProvider>
      </DynamicSolanaSignerProvider>
    </DynamicSolanaJupiterProvider>
  </DynamicSolanaConnectProvider>
);

export default function App({ Component, pageProps, cookies }: Props) {
  const router = useRouter();
  const getHead = Component.getHead ?? (() => <></>);
  const getLayout =
    Component.getLayout ??
    ((page: ReactNode) => <DynamicRegularPageLayout>{page}</DynamicRegularPageLayout>);

  const colorModeManager =
    typeof cookies === 'string' ? cookieStorageManagerSSR(cookies) : localStorageManager;

  // Prefetch next possible routes
  useEffect(() => {
    const prefetchRoutes = async () => {
      // Add routes that are likely to be visited next
      const possibleNextRoutes = ['/vaults', '/directional-vaults', '/press-kit'];

      for (const route of possibleNextRoutes) {
        await router.prefetch(route);
      }
    };

    prefetchRoutes();
  }, [router]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <style global jsx>{`
        html {
          font-family: ${inter.style.fontFamily};
        }
      `}</style>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <StateProvider store={store}>
        <ChakraProvider theme={theme} colorModeManager={colorModeManager}>
          <DynamicRouterHistoryProvider maxHistoryLength={10}>
            <DynamicWeb3ModalProvider>
              <DynamicSignerProvider>
                <DynamicBalanceProvider>
                  <SolanaWrapper>
                    <DynamicInitializeAsyncStorage />
                    <DynamicOnLoadModule />
                    {getHead()}
                    {getLayout(<Component {...pageProps} />)}
                    <ToastContainer />
                  </SolanaWrapper>
                </DynamicBalanceProvider>
              </DynamicSignerProvider>
            </DynamicWeb3ModalProvider>
          </DynamicRouterHistoryProvider>
        </ChakraProvider>
      </StateProvider>
    </>
  );
}

// Ensure color mode is available on the server side
App.getInitialProps = ({ ctx }: { ctx: any }) => ({
  cookies: ctx.req ? (ctx.req.headers.cookie ?? '') : '',
});
